/*
 * Copyright 2025 Intuition Robotics ltd
 */
import {AppPackage_ElliQ, AppPackage_Kaspero, AppPackage_PM, AppPackage_Strickland, AppPackage_TabletElliQ} from "./push-messages.js";
import type {DeviceType} from "./units.js";

export type PushCommand = {
    key: string
    label: string
    data?: string
    confirmationDialogue?: boolean
    targetPackage: string
    mustHaveData?: boolean
    isDisplayed?: boolean
    deviceType?: DeviceType
};

const PC_CR = {key: "on-contacts-updated", isDisplayed: true};
export const PCS_ReloadContacts: PushCommand = {...PC_CR, label: "Reload Contacts SOM", targetPackage: AppPackage_ElliQ, confirmationDialogue: true};
export const PCS_ResetVideoCall: PushCommand = {key: "video-call-clean", label: "Reset Video Call", targetPackage: AppPackage_ElliQ, isDisplayed: true};
const PC_R = {key: "restart-app", confirmationDialogue: true, isDisplayed: true};
export const PCS_RestartSom: PushCommand = {...PC_R, label: "Restart EQ App", targetPackage: AppPackage_ElliQ};
export const PCT_RestartTablet: PushCommand = {...PC_R, label: "Restart RS App", targetPackage: AppPackage_TabletElliQ};
export const PC_RestartStrickland: PushCommand = {...PC_R, label: "Restart Strickland App", targetPackage: AppPackage_Strickland};


export const PC_ProxyPush: PushCommand = {
    key: "proxy-push", label: "Proxy Push", targetPackage: AppPackage_Strickland, data: JSON.stringify({
        "target": "com.ir.pm",
        "otherDevice": true,
        "message": {
            "type": "do_nothing",
            "payload": null
        }
    })
};

export const PC_ShellCommand: PushCommand = {key: "execute-shell-command", label: "Execute Shell Command", targetPackage: AppPackage_Strickland};
export const PC_Ping: PushCommand = {
    key: "do_nothing",
    label: "Ping",
    targetPackage: AppPackage_Strickland,
    confirmationDialogue: false,
    isDisplayed: true
};

export const PC_SpeedTest: PushCommand = {
    key: "speed-test",
    label: "Run Speed Test",
    targetPackage: AppPackage_Strickland,
    confirmationDialogue: false,
    isDisplayed: true
};

export const PC_CloseCaptionOn: PushCommand = {
    key: "toggle-closed-captions",
    label: "Captions On",
    targetPackage: AppPackage_ElliQ,
    data: '{"toggle":true}',
    isDisplayed: true
};

export const PC_CloseCaptionOff: PushCommand = {
    key: "toggle-closed-captions",
    label: "Captions Off",
    targetPackage: AppPackage_ElliQ,
    data: '{"toggle":false}',
    isDisplayed: true
};

export const PC_EnrollWakeword: PushCommand = {
    key: "plan-start",
    label: "Enroll Personalized Wakeword",
    targetPackage: AppPackage_ElliQ,
    data: 'personalized_wakeword.main',
    isDisplayed: true,
    confirmationDialogue: true
};

export const PC_DisenrollWakeword: PushCommand = {
    key: "disenroll-personalized-wakeword",
    label: "Disenroll Personalized Wakeword",
    targetPackage: AppPackage_ElliQ,
    data: '',
    isDisplayed: true,
    confirmationDialogue: true
};

export const PC_ForgetVoiceRecognition: PushCommand = {
    key: "forget-voice-recognition",
    label: "Delete Voice ID",
    targetPackage: AppPackage_ElliQ,
    data: '',
    isDisplayed: true,
    confirmationDialogue: true
};

export const PC_TriggerAvailability: PushCommand = {
    key: "execute-shell-command",
    label: "Trigger Availability",
    targetPackage: AppPackage_Strickland,
    data: 'am broadcast -a LocalLoad -n com.ir.ai.kyou/.receivers.ElliqReceiver -e action "triggerAvailability"',
    isDisplayed: true
};

export const PC_AudioCalibration: PushCommand = {
    key: "plan-start",
    label: "Audio Calibration",
    targetPackage: AppPackage_ElliQ,
    data: 'audio_calibration.main',
    isDisplayed: true,
    confirmationDialogue: true
};

export const PC_Reboot = {
    key: "device_reboot",
    label: "Reboot Device",
    targetPackage: AppPackage_Strickland,
    confirmationDialogue: true,
    isDisplayed: true
};
export const PC_RebootByKaspero = {
    key: "support-device-reboot",
    label: "Reboot Device",
    targetPackage: AppPackage_Kaspero
};
export const PCT_ShutdownTablet: PushCommand = {
    key: "device_shutdown",
    label: "Shutdown Device",
    targetPackage: AppPackage_PM,
    confirmationDialogue: true,
    isDisplayed: true
};
export const PCT_EnableKiosk: PushCommand = {
    key: "set_kiosk_mode",
    label: "Enable Kiosk",
    data: "true",
    targetPackage: AppPackage_TabletElliQ,
    confirmationDialogue: true,
    isDisplayed: true
};
export const PCT_DisableKiosk: PushCommand = {
    key: "set_kiosk_mode",
    label: "Disable Kiosk",
    data: "false",
    targetPackage: AppPackage_TabletElliQ,
    confirmationDialogue: true,
    isDisplayed: true
};
const PCK_B = {key: "set_bt_adapter", confirmationDialogue: true, isDisplayed: true};
const PC_EB = {...PCK_B, data: "true"};
const PC_DB = {...PCK_B, data: "false"};
export const PCS_EnableBluetooth: PushCommand = {...PC_EB, targetPackage: AppPackage_ElliQ, label: "Enable Bluetooth SOM"};
export const PCS_DisableBluetooth: PushCommand = {...PC_DB, targetPackage: AppPackage_ElliQ, label: "Disable Bluetooth SOM"};
export const PCT_EnableBluetooth: PushCommand = {...PC_EB, targetPackage: AppPackage_TabletElliQ, label: "Enable Bluetooth TABLET"};
export const PCT_DisableBluetooth: PushCommand = {...PC_DB, targetPackage: AppPackage_TabletElliQ, label: "Disable Bluetooth TABLET"};
export const PC_UL = {key: "upload-logs", isDisplayed: true};
export const PCS_UploadLogs: PushCommand = {...PC_UL, targetPackage: AppPackage_ElliQ, label: "Upload Logs"};
export const PCT_UploadLogs: PushCommand = {...PC_UL, targetPackage: AppPackage_TabletElliQ, label: "Upload Logs"};
export const PC_UploadLogsViaStrickland: PushCommand = {key: "upload-logs-to", isDisplayed: true, targetPackage: AppPackage_Strickland, label: "Upload Logs"};

const PC_UR = {key: "update_runtime_status", isDisplayed: true};
export const PCS_UpdateStatus: PushCommand = {...PC_UR, label: "Refresh Status SOM", targetPackage: AppPackage_ElliQ};
export const PCT_UpdateStatus: PushCommand = {...PC_UR, label: "Refresh Status TABLET", targetPackage: AppPackage_TabletElliQ};
export const PCT_ResetHandshake: PushCommand = {
    key: "reset-handshake",
    label: "Reset Handshake",
    targetPackage: AppPackage_Strickland,
    confirmationDialogue: true,
    isDisplayed: true,
    deviceType: "tablet"
};
export const PCS_PressKnob: PushCommand = {
    key: "press-knob",
    label: "Press Knob",
    targetPackage: AppPackage_ElliQ,
    confirmationDialogue: true,
    isDisplayed: true
};
const PC_CC = {key: "clear-cache", confirmationDialogue: true, isDisplayed: true};
export const PCS_ClearCache: PushCommand = {...PC_CC, targetPackage: AppPackage_ElliQ, label: "Clear Cache SOM"};
export const PCT_ClearCache: PushCommand = {...PC_CC, targetPackage: AppPackage_TabletElliQ, label: "Clear Cache TABLET"};

const PC_SRS = {
    key: "schedule_runtime_status_in_seconds",
    confirmationDialogue: true,
    isDisplayed: true,
    mustHaveData: true,
    data: JSON.stringify({
        "interval": "30",
        "duration": "300"
    })
};
export const PCS_ScheduleRuntimeStatus: PushCommand = {...PC_SRS, targetPackage: AppPackage_ElliQ, label: "Schedule Runtime Status SOM"};
export const PCT_ScheduleRuntimeStatus: PushCommand = {...PC_SRS, targetPackage: AppPackage_TabletElliQ, label: "Schedule Runtime Status TABLET"};

export const PCS_StartSticky: PushCommand = {
    key: "start-sticky",
    targetPackage: AppPackage_ElliQ,
    mustHaveData: true,
    label: "Start Sticky",
    data: JSON.stringify({
        "previousPlan": "",
        "stickyPlan": "",
        "expressionName": "@",
        "aproposTag": ""
    })
};

export const PCS_EnableWifiAdb = {
    key: "enable-wifi-adb",
    targetPackage: AppPackage_Strickland,
    label: "Enables adb over wifi. Copy the command result and paste to your terminal"
};
// Known SOM (ElliQ app) push actions, from the robot's PushAction registry (mono-repo).
// Free-text keys are also accepted in the support tool; these just make the common ones discoverable.
const som = (key: string, label: string, extra: Partial<PushCommand> = {}): PushCommand => ({key, label, targetPackage: AppPackage_ElliQ, ...extra});

export const PCS_KnownCommands: PushCommand[] = [
    som("set-content-version", "Set Content Version (hot content deploy, 4th segment only)", {mustHaveData: true, data: "2-32-2-5"}),
    som("false-keyword-detector-load", "Load False Keyword Detector Model", {mustHaveData: true, data: JSON.stringify({modelVersion: "v2"})}),
    som("ml-model-load", "Load ML Model", {mustHaveData: true}),
    som("refresh-unit-config", "Refresh Unit Config (optional comma-separated sections)"),
    som("upload-heap", "Upload Heap Dump"),
    som("get-free-space", "Get Free Space"),
    som("clear-logs", "Clear Logs"),
    som("plan-stop", "Stop Plan", {mustHaveData: true}),
    som("fire-intent", "Fire Intent", {mustHaveData: true}),
    som("fire-intent-with-params", "Fire Intent With Params (JSON)", {mustHaveData: true}),
    som("trigger-notification", "Trigger Test Notification", {data: JSON.stringify({notificationType: "live", planName: "messaging"})}),
    som("set-speech-tone", "Set Speech Tone (polite / casual)", {mustHaveData: true, data: "polite"}),
    som("set-user-available", "Set User Available"),
    som("set-user-unavailable", "Set User Unavailable"),
    som("cameras-on", "Cameras On"),
    som("cameras-off", "Cameras Off"),
    som("volume-up", "Volume Up"),
    som("volume-down", "Volume Down"),
    som("measure-mic-db-level", "Measure Mic dB Level"),
    som("publish-states", "Publish States"),
    som("flush-facts", "Flush Facts"),
    som("restart-sensory-listener", "Restart Sensory Listener"),
    som("reset-capping-data", "Reset Capping Data", {confirmationDialogue: true}),
    som("reset-spacing-data", "Reset Spacing Data", {confirmationDialogue: true}),
    som("reset-goals", "Reset Goals", {confirmationDialogue: true})
];

export const pushMessages: PushCommand[] = [
    {
        key: "set-wifi-all",
        targetPackage: AppPackage_TabletElliQ,
        label: "Reset handshake and look for all wifi GHz range (also 5 GHz)"
    },
    {
        key: "set-wifi-24",
        targetPackage: AppPackage_TabletElliQ,
        label: "Reset handshake and look for wifi of 2.4 GHz range"
    },
    {
        // notAvailableOnUnits: ["tablet"],
        key: "maintenance-off",
        label: "Exits the unit from maintenance mode",
        targetPackage: AppPackage_Kaspero
    },
    {
        // notAvailableOnUnits: ["tablet"],
        key: "maintenance-on",
        label: "Enters the unit from maintenance mode",
        targetPackage: AppPackage_Kaspero
    },
    {
        key: "support-device-unpair",
        label: "Unpair bluetooth (similar to kasper screen button)",
        targetPackage: AppPackage_Kaspero
    },
    {
        key: "support-identity-provision",
        label: "Hard provision (similar to kasper screen button)",
        targetPackage: AppPackage_Kaspero
    },
    {
        key: "support-kasper-app-restart",
        label: "Restart the kasper app",
        targetPackage: AppPackage_Kaspero
    },
    {
        key: "support-main-app-restart",
        label: "Restart the main app",
        targetPackage: AppPackage_Kaspero
    },
    {
        key: "support-teamviewer-start",
        label: "Start team-viewer app (if installed)",
        targetPackage: AppPackage_Kaspero
    },
    {
        key: "support-teamviewer-stop",
        label: "Stop team-viewer app (if installed)",
        targetPackage: AppPackage_Kaspero
    },
    {
        key: "uninstall-app",
        label: "Uninstalls an app",
        mustHaveData: true,
        targetPackage: AppPackage_Kaspero
    },
    {
        key: "clear-app-data",
        label: "KS - Clears the data for a certain app",
        mustHaveData: true,
        targetPackage: AppPackage_Kaspero
    },
    {
        key: "clear-app-data",
        label: "ST - Clears the data for a certain app",
        mustHaveData: true,
        targetPackage: AppPackage_Strickland
    },
    {
        key: "launch-app",
        label: "Starts an app",
        mustHaveData: true,
        targetPackage: AppPackage_Kaspero
    },
    {
        key: "pm-change-env",
        targetPackage: AppPackage_PM,
        label: "Changes environment of the PM\n" + "(dev - staging - prod)",
        mustHaveData: true
    },
    {
        key: "pm-upload-status",
        targetPackage: AppPackage_PM,
        label: "Send PM runtime status"
    },
    {
        key: "pm-resume-installation",
        targetPackage: AppPackage_PM,
        label: "Remotely resume installation for a device"
    },
    {
        key: "pm-install-now",
        targetPackage: AppPackage_Strickland,
        label: "Remotely updates a device based on the current config"
    },
    {
        key: "pm-cancel-installation",
        targetPackage: AppPackage_Strickland,
        label: "Cancel installation"
    },
    {
        key: "pm-download-apks",
        targetPackage: AppPackage_PM,
        label: "Download apks on device"
    },
    {
        key: "pm-enable-auto-installation",
        targetPackage: AppPackage_PM,
        label: "Remotely enable auto-installations in the PM"
    },
    {
        key: "pm-disable-auto-installation",
        targetPackage: AppPackage_PM,
        label: "Remotely disable auto-installations in the PM"
    },
    {
        key: "pm-enable-downgrades",
        targetPackage: AppPackage_PM,
        label: "Remotely enable downgrades in the PM"
    },
    {
        key: "pm-disable-downgrades",
        targetPackage: AppPackage_PM,
        label: "Remotely disable downgrades in the PM"
    },
    {
        key: "clear-expired-cache",
        targetPackage: AppPackage_Kaspero,
        // notAvailableOnUnits: ["som"],
        label: "Clear all data that we are not using anymore"
    },
    {
        key: "clear-all-cache-data",
        // notAvailableOnUnits: ["som"],
        targetPackage: AppPackage_TabletElliQ,
        label: "Clear all data from /sdcard/cache that contain all images and videos of the Remote screen app"
    },
    {
        key: "toggle-face-collection",
        label: "Toggle Face Collection",
        // notAvailableOnUnits: ["tablet"],
        targetPackage: AppPackage_ElliQ
    },
    PCS_EnableWifiAdb,
    PCS_StartSticky,
    PC_Ping,
    PCS_RestartSom,
    PCT_RestartTablet,
    PC_Reboot,
    PC_RebootByKaspero,
    PCS_ReloadContacts,
    PCS_ResetVideoCall,
    PCS_EnableBluetooth,
    PCS_DisableBluetooth,
    PCS_UploadLogs,
    PCS_UpdateStatus,
    PCS_PressKnob,
    PCS_ClearCache,
    PCS_ScheduleRuntimeStatus,
    PCT_ShutdownTablet,
    PCT_EnableKiosk,
    PCT_DisableKiosk,
    PCT_EnableBluetooth,
    PCT_DisableBluetooth,
    PCT_UploadLogs,
    PCT_UpdateStatus,
    PCT_ResetHandshake,
    PCT_ClearCache,
    PCT_ScheduleRuntimeStatus,
    PC_ProxyPush,
    PC_ShellCommand,
    PC_SpeedTest,
    PC_CloseCaptionOn,
    PC_CloseCaptionOff,
    PC_EnrollWakeword,
    PC_DisenrollWakeword,
    PC_ForgetVoiceRecognition,
    PC_TriggerAvailability,
    PC_AudioCalibration,
    ...PCS_KnownCommands
];
